// Shared TypeScript interfaces for the Lumaprints Product Configurator block.

export interface LumaSize {
	width: number;
	height: number;
	/** True when soft DPI mode is active and this size falls below the required resolution threshold. */
	belowDpi?: boolean;
}

export interface LumaOptionItem {
	optionId: number;
	optionName: string;
}

export interface LumaOptionGroup {
	groupName: string;
	items: LumaOptionItem[];
}

export interface LumaSubcategory {
	subcategoryId: number;
	name: string;
	/** When set, shown in the configurator and cart instead of `name`; `name` stays the synced Lumaprints title. */
	displayName?: string | null;
	minWidth: number;
	maxWidth: number;
	minHeight: number;
	maxHeight: number;
	requiredDpi: number;
	availableSizes: LumaSize[];
	/** Framed fine art: max glass opening from GlassLimits (inches). */
	usesGlassMatRules?: boolean;
	maxGlassWidth?: number;
	maxGlassHeight?: number;
	optionGroups: LumaOptionGroup[];
	/** Option groups configured as auto-assigned in the admin. groupName → optionId. */
  autoAssignedOptions: Record<string, { optionId: number; optionName: string }>;
}

export interface ConfiguratorConfig {
	/** Present on REST responses when the product is purchasable; ignored by UI logic. */
	available?: true;
	subcategories: LumaSubcategory[];
	multiplier: number;
	contactPageUrl: string;
	nonce: string;
	addToCartNonce: string;
	imageAspectRatio: number | null;
	/** Number of production images on the product (1 for single, 2–5 for multi-panel). */
	productionImageCount: number;
}

/** Customer-facing subcategory title (admin override or Lumaprints `name`). */
export function subcategoryDisplayLabel(subcat: LumaSubcategory): string {
	const t = subcat.displayName?.trim();
	return t ? t : subcat.name;
}

/** REST / bootstrap payload when the product must not show the configurator. */
export interface ConfiguratorUnavailablePayload {
	available: false;
	message: string;
}

export interface PricingRequest {
	subcategoryId: number;
	size: LumaSize;
	options: number[];
}

export interface PricingResponse {
	basePrice: number;
	displayedPrice: number;
	multiplier: number;
	options: Array<{
		optionId: number;
		optionGroupName: string;
		optionName: string;
		price: number;
	}>;
}

export interface ConfiguratorState {
	config: ConfiguratorConfig | null;
	selectedSubcategoryId: number | null;
	selectedSize: LumaSize | null;
	selectedOptions: Record<string, number>; // groupName → optionId
	basePrice: number | null;
	displayedPrice: number | null;
	isLoadingConfig: boolean;
	isLoadingPrice: boolean;
	isAddingToCart: boolean;
	error: string | null;
}

// Bootstrap data injected by the PHP template into data-bootstrap attribute.
export interface BootstrapData {
	productId: number;
	productName: string;
	restUrl: string;
	restNonce: string;
	addToCartNonce: string;
	cartUrl: string;
	/** When false, the template shows an unavailable message and init skips loading config. */
	available?: boolean;
	unavailableMessage?: string;
}

export function isConfiguratorUnavailablePayload(
	value: unknown,
): value is ConfiguratorUnavailablePayload {
	return (
		typeof value === 'object'
		&& value !== null
		&& (value as { available?: unknown }).available === false
		&& typeof (value as { message?: unknown }).message === 'string'
	);
}

// Global injected by setup.php via wp_localize_script.
declare global {
	interface Window {
		lumaGlobal?: {
			restUrl: string;
			restNonce: string;
		};
	}
}
